import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';
import { useMemo } from 'react';
import { usePartnerProfileConfig } from '../Enrollment/usePartnerProfileConfig';
import { SanitizeHTML } from '../../Content/SanitizeHTML';

const BENEFITS_CONTAINER_ID = 'partner-benefits-container';

/**
 * Splits a selector string by commas, respecting parentheses nesting.
 * This handles pseudo-classes like :is(.a, .b) and :nth-child(2n + 1) correctly.
 */
function splitSelectorByComma(selector: string): string[] {
  const parts: string[] = [];
  let current = '';
  let parenDepth = 0;

  for (let i = 0; i < selector.length; i += 1) {
    const char = selector[i];
    if (char === '(') {
      parenDepth += 1;
      current += char;
    } else if (char === ')') {
      parenDepth -= 1;
      current += char;
    } else if (char === ',' && parenDepth === 0) {
      parts.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    parts.push(current.trim());
  }

  return parts;
}

/**
 * Scopes a CSS selector to the Benefits container ID.
 */
function scopeSelector(selector: string): string {
  return splitSelectorByComma(selector)
    .map((s) => {
      const trimmed = s.trim();
      if (!trimmed) return '';
      // If selector already contains the container ID, don't double-scope
      if (trimmed.includes(`#${BENEFITS_CONTAINER_ID}`)) {
        return trimmed;
      }
      return `#${BENEFITS_CONTAINER_ID} ${trimmed}`;
    })
    .filter(Boolean)
    .join(', ');
}

/**
 * Scopes CSS rules inside an at-rule block (e.g., @media).
 */
function scopeInnerCss(css: string): string {
  let result = '';
  let idx = 0;

  while (idx < css.length) {
    let selector = '';
    while (idx < css.length && css[idx] !== '{') {
      selector += css[idx];
      idx += 1;
    }

    if (selector.trim()) {
      result += scopeSelector(selector);
    }

    if (css[idx] === '{') {
      let braceCount = 1;
      result += '{';
      idx += 1;

      while (idx < css.length && braceCount > 0) {
        if (css[idx] === '{') braceCount += 1;
        else if (css[idx] === '}') braceCount -= 1;
        result += css[idx];
        idx += 1;
      }
    }
  }

  return result;
}

/**
 * Scopes CSS rules to the Benefits container ID to prevent style bleeding.
 * Supports media queries - scopes rules inside media queries as well.
 * @param css - Raw CSS string
 * @returns CSS with all selectors scoped to #partner-benefits-container
 */
function scopeCssToContainer(css: string): string {
  if (!css) return '';

  // Remove potentially dangerous content
  const sanitized = css
    .replace(/@import\s+[^;]+;?/gi, '') // Remove @import statements (prevents external CSS loading)
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/expression\s*\(/gi, '') // Remove CSS expressions (IE)
    .replace(/url\s*\(\s*["']?\s*data:/gi, 'url(blocked:') // Block data URLs in url()
    .replace(/url\s*\(\s*["']?\s*https?:/gi, 'url(blocked:'); // Block external URLs (tracking prevention)

  // Process CSS: scope selectors while preserving media queries
  let result = '';
  let idx = 0;

  // At-rules that should NOT have their contents scoped (they contain declarations, not selectors)
  const noScopeAtRules = ['@keyframes', '@font-face', '@charset', '@namespace'];

  while (idx < sanitized.length) {
    // Check for @media or other at-rules
    if (sanitized[idx] === '@') {
      const atRuleMatch = sanitized.slice(idx).match(/^@[\w-]+/);
      if (atRuleMatch) {
        const atRuleName = atRuleMatch[0].toLowerCase();
        const shouldScope = !noScopeAtRules.includes(atRuleName);

        // Capture the full at-rule header (e.g., "@media (max-width: 600px)")
        const headerMatch = sanitized.slice(idx).match(/^@[\w-]+\s*[^{]*/);
        const atRuleHeader = headerMatch ? headerMatch[0] : atRuleName;
        result += atRuleHeader;
        idx += atRuleHeader.length;

        // Find the opening brace
        while (idx < sanitized.length && sanitized[idx] !== '{') {
          result += sanitized[idx];
          idx += 1;
        }

        if (sanitized[idx] === '{') {
          result += '{';
          idx += 1;

          // Process content inside the at-rule block
          let braceCount = 1;
          let innerContent = '';

          while (idx < sanitized.length && braceCount > 0) {
            if (sanitized[idx] === '{') braceCount += 1;
            else if (sanitized[idx] === '}') braceCount -= 1;

            if (braceCount > 0) {
              innerContent += sanitized[idx];
            }
            idx += 1;
          }

          // Only scope inner content for at-rules that contain style rules (like @media, @supports)
          if (shouldScope) {
            result += scopeInnerCss(innerContent);
          } else {
            result += innerContent;
          }
          result += '}';
        }
      } else {
        result += sanitized[idx];
        idx += 1;
      }
    } else {
      // Regular rule - find selector and scope it
      let selector = '';
      while (idx < sanitized.length && sanitized[idx] !== '{') {
        selector += sanitized[idx];
        idx += 1;
      }

      if (selector.trim()) {
        result += scopeSelector(selector);
      }

      // Copy the rest of the rule block
      if (sanitized[idx] === '{') {
        let braceCount = 1;
        result += '{';
        idx += 1;

        while (idx < sanitized.length && braceCount > 0) {
          if (sanitized[idx] === '{') braceCount += 1;
          else if (sanitized[idx] === '}') braceCount -= 1;
          result += sanitized[idx];
          idx += 1;
        }
      }
    }
  }

  return result;
}

/**
 * Extracts <style> tags from HTML content and returns scoped CSS and remaining HTML.
 */
function extractAndScopeStyles(html: string): { css: string; html: string } {
  if (!html) return { css: '', html: '' };

  const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  const styles: string[] = [];
  let match = styleRegex.exec(html);

  while (match !== null) {
    styles.push(match[1]);
    match = styleRegex.exec(html);
  }

  const htmlWithoutStyles = html.replace(styleRegex, '');
  const scopedCss = styles.map((s) => scopeCssToContainer(s)).join('\n');

  return { css: scopedCss, html: htmlWithoutStyles };
}

export const PartnerBenefitsSection = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { partnerBenefits } = usePartnerProfileConfig();

  // Memoize filtered and sorted benefits to avoid recreating on every render
  const benefits = useMemo(() => {
    if (!partnerBenefits) return null;
    return partnerBenefits
      .filter((benefit) => benefit.isEnabled)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [partnerBenefits]);

  // Extract and scope CSS from all non-benefit items' descriptions
  const scopedCss = useMemo(() => {
    if (!partnerBenefits) return null;
    const allCss = partnerBenefits
      .filter((b) => b.isEnabled && !(b.isBenefit ?? true))
      .map((b) => {
        const titleStyles = extractAndScopeStyles(b.title || '').css;
        const descStyles = extractAndScopeStyles(b.description || '').css;
        return [titleStyles, descStyles].filter(Boolean).join('\n');
      })
      .filter(Boolean)
      .join('\n');
    return allCss || null;
  }, [partnerBenefits]);

  return benefits?.length === 0 ? null : (
    <>
      {scopedCss && (
        <style dangerouslySetInnerHTML={{ __html: scopedCss }} />
      )}
      <Box
        id={BENEFITS_CONTAINER_ID}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: theme.spacing(1.5),
          alignSelf: 'stretch',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
          }}
        >
          <Typography variant='h4'>{t('partnerDetails.benefits')}</Typography>
          <Typography variant='body8' color={theme.palette.grey[700]}>{t('partnerDetails.benefitsSubHead')}</Typography>
        </Box>
        <Box>
          {benefits?.map((item, index) => {
            const isBenefit = item.isBenefit ?? true;

            if (!isBenefit) {
              // Extract style tags (they're injected separately as scoped CSS)
              const titleContent = extractAndScopeStyles(item.title || '').html;
              const descContent = extractAndScopeStyles(item.description || '').html;

              return (
                <Box
                  data-testid={`partners-individual-benefits-${index}`}
                  key={index}
                >
                  {titleContent && (
                    <Box>
                      <Typography variant='h15'
                        color={theme.palette.grey[700]}
                        fontWeight={400}
                        component="div"
                      >
                        <SanitizeHTML
                          html={titleContent}
                          options={{
                            allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'div', 'span']),
                            allowedAttributes: {
                              ...sanitizeHtml.defaults.allowedAttributes,
                              div: ['style', 'class', 'className'],
                              span: ['style', 'class', 'className'],
                              img: ['src', 'alt', 'style', 'class', 'className', 'width', 'height'],
                            },
                          }}
                        />
                      </Typography>
                    </Box>
                  )}
                  {descContent && (
                    <Box>
                      <SanitizeHTML
                        html={descContent}
                        options={{
                          allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'div', 'span']),
                          allowedAttributes: {
                            ...sanitizeHtml.defaults.allowedAttributes,
                            div: ['style', 'class', 'className'],
                            span: ['style', 'class', 'className'],
                            img: ['src', 'alt', 'style', 'class', 'className', 'width', 'height'],
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            }

            return (
              <ListItem disableGutters
                data-testid={`partners-individual-benefits-${index}`}
                key={index}
                sx={{
                  display: 'block',
                  p: 0,
                  lineHeight: 'normal',
                }}>
                {item.title && (
                  <Typography variant='h15'
                    color={theme.palette.grey[700]}
                    fontWeight={700}
                  >
                    • {item.title}
                  </Typography>
                )}
                <List sx={{ p: 0, pl: 3 }}>
                  <ListItem
                    sx={{ p: 0 }}
                    disableGutters>
                    <ListItemText>
                      <Typography
                        variant="body8"
                        color={theme.palette.grey[700]}
                      >{item.description}</Typography>
                    </ListItemText>
                  </ListItem>
                </List>
              </ListItem>
            );
          })}
        </Box>
      </Box >
      <Divider />
    </>
  );
};
